/**
 * RecipeBookPage — The Recipe Book.
 *
 * A quiet family cookbook inside the Residence: editorial catalogue
 * (All recipes / the Kitchen's categories) → one recipe page → print.
 *
 * Reuses the Kitchen's category system, recipe store, image persistence
 * (recipe.imageUrl → core slice → backend) and suitability engine.
 * Screen shows the Residence cookbook; print shows the practical
 * single-recipe sheet (RecipePrintCard). Presentation only.
 */
import { useMemo, useState } from "react";
import { toast } from "sonner";
import BetaFeedbackButton from "../../components/BetaFeedbackButton";
import { useHomeOSState } from "./state/HomeOSStateProvider";
import { RECIPE_BOOK_CATEGORIES, matchesRecipeCategory } from "./data/mealsData";
import { RecipeEditModal, HouseholdSuitability } from "./components/MealsModals";
import { RecipeAddToSlotModal } from "./components/RecipeAddToSlotModal";
import { RecipePhotoImportModal } from "./components/RecipePhotoImportModal";
import { RecipePrintCard, RecipePrintStyles, pickRecipeImage } from "./components/RecipePrintCard";
import {
  ResidenceLayout, PageHeading, EditorialSection,
  Caption, Rule, TextLink, PillButton, Colophon, useLightPhase,
} from "./components/os/editorial";
import { ResidenceRail } from "./components/os/ResidenceRail";
import { getHouseholdFoodContext, evaluateMealForHousehold } from "./utils/foodIntelligence";
import plateArt from "./assets/watercolour/meals.webp";

const SERIF = "var(--ara-font-display), 'Times New Roman', serif";
const SANS = "Inter, system-ui, sans-serif";

/* Universal recipe placeholder — the Kitchen's watercolour plate. */
function RecipeImage({ recipe, size = "thumb" }) {
  const hasPhoto = Boolean(recipe.imageUrl);
  if (size === "thumb") {
    return (
      <span
        className="w-14 h-14 shrink-0 overflow-hidden flex items-center justify-center"
        style={{ background: hasPhoto ? "transparent" : "var(--ara-paper, #FAF9F6)", border: "1px solid var(--ara-line-soft)", borderRadius: "4px" }}
        aria-hidden
        data-testid={`recipe-image-${hasPhoto ? "photo" : "placeholder"}-${recipe.id}`}
      >
        {hasPhoto
          ? <img src={recipe.imageUrl} alt="" className="w-full h-full object-cover" />
          : <img src={plateArt} alt="" className="w-full h-full object-contain" style={{ opacity: 0.8, padding: "5px" }} />}
      </span>
    );
  }
  return (
    <div
      className="w-full overflow-hidden flex items-center justify-center"
      style={{
        border: "1px solid var(--ara-line-soft)",
        borderRadius: "4px",
        background: hasPhoto ? "transparent" : "var(--ara-paper, #FAF9F6)",
        maxHeight: hasPhoto ? "440px" : "300px",
        minHeight: hasPhoto ? undefined : "240px",
      }}
      data-testid={`recipe-hero-image-${hasPhoto ? "photo" : "placeholder"}`}
    >
      {hasPhoto
        ? <img src={recipe.imageUrl} alt={recipe.title} className="w-full object-cover" style={{ maxHeight: "440px" }} />
        : <img src={plateArt} alt="" className="object-contain" style={{ opacity: 0.85, maxHeight: "240px", padding: "24px" }} />}
    </div>
  );
}

export default function RecipeBookPage() {
  const state = useHomeOSState();
  const { recipes, members, kitchenCookingFor, toggleRecipeFavourite, duplicateRecipe, removeRecipe, upsertRecipe } = state;
  const { phase } = useLightPhase();

  const [categoryId, setCategoryId] = useState(null); // null = All recipes
  const [viewId, setViewId] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [photoImportOpen, setPhotoImportOpen] = useState(false);
  const [recipeForAdd, setRecipeForAdd] = useState(null);

  const all = useMemo(() => recipes || [], [recipes]);

  const categoryCounts = useMemo(() => {
    const m = {};
    RECIPE_BOOK_CATEGORIES.forEach(c => { m[c.id] = all.filter(r => matchesRecipeCategory(r, c)).length; });
    return m;
  }, [all]);

  const populatedCategories = RECIPE_BOOK_CATEGORIES.filter(c => (categoryCounts[c.id] || 0) > 0);
  const activeCategory = categoryId ? RECIPE_BOOK_CATEGORIES.find(c => c.id === categoryId) : null;

  const catalogue = useMemo(() => {
    if (!activeCategory) return all;
    return all.filter(r => matchesRecipeCategory(r, activeCategory));
  }, [all, activeCategory]);

  const viewRecipe = viewId ? all.find(r => r.id === viewId) || null : null;

  const suitability = useMemo(() => {
    if (!viewRecipe) return null;
    const ctx = getHouseholdFoodContext(members, kitchenCookingFor);
    if (!ctx.hasAnyFoodInfo) return null;
    return evaluateMealForHousehold({ recipe: viewRecipe }, ctx);
  }, [viewRecipe, members, kitchenCookingFor]);

  const openAdd = () => { setEditId(null); setEditDraft(null); setEditOpen(true); };
  const openEdit = (recipe) => { setEditId(recipe.id); setEditDraft(null); setEditOpen(true); };
  const changePhoto = (recipe) => pickRecipeImage((dataUrl) => {
    if (!dataUrl) return;
    upsertRecipe({ ...recipe, imageUrl: dataUrl });
    toast.success("Photograph added.");
  });
  const removePhoto = (recipe) => { upsertRecipe({ ...recipe, imageUrl: "" }); toast.success("Photograph removed."); };

  const sharedModals = (
    <>
      <RecipeEditModal
        open={editOpen}
        recipeId={editId}
        initialDraft={editDraft}
        onClose={() => { setEditOpen(false); setEditDraft(null); }}
      />
      <RecipeAddToSlotModal open={!!recipeForAdd} recipe={recipeForAdd} onClose={() => setRecipeForAdd(null)} />
    </>
  );

  /* ── Single recipe — one quiet cookbook page ── */
  if (viewRecipe) {
    const metaBits = [
      viewRecipe.prepTime ? `Prep ${viewRecipe.prepTime} min` : null,
      viewRecipe.cookTime ? `Cook ${viewRecipe.cookTime} min` : null,
      viewRecipe.serves ? `Serves ${viewRecipe.serves}` : null,
      viewRecipe.difficulty || null,
    ].filter(Boolean);

    return (
      <ResidenceLayout rail={<ResidenceRail currentKey="recipes" />} phase={phase} testid="module-page-recipe-book">
        <RecipePrintStyles />
        <style>{`
          .rb-print-sheet { display: none; }
          @media print {
            .rb-screen, [data-testid="residence-rail"], [data-testid="residence-mobile-bar"], footer { display: none !important; }
            .rb-print-sheet { display: block !important; }
          }
        `}</style>

        <div className="rb-screen" data-testid="recipe-book-detail">
          <div className="pt-2 pb-8">
            <TextLink onClick={() => setViewId(null)} testid="recipe-book-back-to-catalogue">
              ← Back to Recipe Book
            </TextLink>
          </div>

          <Caption>Recipe{viewRecipe.category ? ` · ${viewRecipe.category}` : ""}{viewRecipe.favourite ? " · Family favourite" : ""}</Caption>
          <h1
            className="mt-3 text-4xl sm:text-5xl leading-[1.05]"
            style={{ fontFamily: SERIF, color: "var(--ara-ink)", fontWeight: 500, letterSpacing: "-0.01em" }}
            data-testid="recipe-book-detail-title"
          >
            {viewRecipe.title}
          </h1>
          {viewRecipe.subtitle && (
            <p className="mt-3 italic text-[17px] leading-[1.5]" style={{ fontFamily: SERIF, color: "var(--ara-ink-soft)", fontWeight: 400, maxWidth: "44ch" }}>
              {viewRecipe.subtitle}
            </p>
          )}

          <div className="mt-8">
            <RecipeImage recipe={viewRecipe} size="hero" />
            <div className="mt-3 flex flex-wrap items-baseline gap-x-6">
              {viewRecipe.imageUrl ? (
                <>
                  <TextLink onClick={() => changePhoto(viewRecipe)} testid="recipe-book-replace-photo">Replace photograph</TextLink>
                  <TextLink onClick={() => removePhoto(viewRecipe)} testid="recipe-book-remove-photo">Remove photograph</TextLink>
                </>
              ) : (
                <TextLink onClick={() => changePhoto(viewRecipe)} testid="recipe-book-add-photo">Add a photograph</TextLink>
              )}
            </div>
          </div>

          {metaBits.length > 0 && (
            <>
              <div className="mt-10" />
              <Rule soft />
              <p className="py-4 text-[13px] tracking-wide" style={{ fontFamily: SANS, color: "var(--ara-ink-muted)", fontWeight: 400 }} data-testid="recipe-book-detail-meta">
                {metaBits.join("  ·  ")}
              </p>
              <Rule soft />
            </>
          )}

          {suitability && suitability.status !== "none" && (
            <div className="mt-8" data-testid="recipe-book-suitability">
              <HouseholdSuitability evaluation={suitability} />
            </div>
          )}

          <div className="mt-12 grid grid-cols-1 md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] gap-x-16 gap-y-12">
            <section data-testid="recipe-book-ingredients">
              <Caption>Ingredients</Caption>
              <Rule soft className="mt-3" />
              <ul className="mt-1">
                {(viewRecipe.ingredients || []).map((ing, i, arr) => (
                  <li
                    key={i}
                    className={`py-3 text-[15px] leading-[1.5] ${i === arr.length - 1 ? "" : "border-b"}`}
                    style={{ fontFamily: SANS, color: "var(--ara-ink)", fontWeight: 400, borderColor: "var(--ara-line-soft)" }}
                  >
                    {ing}
                  </li>
                ))}
              </ul>
            </section>

            <section data-testid="recipe-book-method">
              <Caption>Method</Caption>
              <Rule soft className="mt-3" />
              <ol className="mt-5 space-y-6">
                {(viewRecipe.instructions || []).map((step, i) => (
                  <li key={i} className="grid grid-cols-[auto_1fr] gap-5 items-baseline">
                    <span className="text-[15px] tabular-nums" style={{ fontFamily: SERIF, color: "var(--ara-ink-soft)", fontWeight: 500 }}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <p className="text-[15.5px] leading-[1.65]" style={{ fontFamily: SANS, color: "var(--ara-ink)", fontWeight: 400 }}>
                      {step}
                    </p>
                  </li>
                ))}
              </ol>
            </section>
          </div>

          {viewRecipe.notes && (
            <div className="mt-12" data-testid="recipe-book-notes">
              <Caption>Notes</Caption>
              <p className="mt-3 italic text-[17px] leading-[1.6]" style={{ fontFamily: SERIF, color: "var(--ara-ink-soft)", fontWeight: 400, maxWidth: "52ch" }}>
                {viewRecipe.notes}
              </p>
            </div>
          )}

          <div className="mt-14">
            <Rule soft />
            <div className="pt-6 flex flex-wrap items-baseline gap-x-8 gap-y-3" data-testid="recipe-book-detail-actions">
              <TextLink onClick={() => openEdit(viewRecipe)} testid="recipe-book-edit-btn">Edit recipe</TextLink>
              <TextLink onClick={() => setRecipeForAdd(viewRecipe)} testid="recipe-book-add-to-plan-btn">Add to meal plan</TextLink>
              <TextLink onClick={() => window.print()} testid="recipe-book-print-btn">Print this recipe</TextLink>
              <TextLink onClick={() => toggleRecipeFavourite(viewRecipe.id)} testid="recipe-book-fav-btn">
                {viewRecipe.favourite ? "♥ Favourite" : "♡ Mark as favourite"}
              </TextLink>
              <TextLink onClick={() => { duplicateRecipe(viewRecipe.id); toast.success("Recipe duplicated."); }} testid="recipe-book-duplicate-btn">
                Duplicate
              </TextLink>
              <TextLink
                onClick={() => {
                  if (window.confirm(`Delete "${viewRecipe.title}"? This can't be undone.`)) {
                    removeRecipe(viewRecipe.id);
                    toast.success("Recipe deleted.");
                    setViewId(null);
                  }
                }}
                testid="recipe-book-delete-btn"
              >
                Delete
              </TextLink>
            </div>
          </div>

          <Colophon>ARA · Recipe Book</Colophon>
        </div>

        {/* Print — practical single-recipe sheet only */}
        <div className="rb-print-sheet" data-testid="recipe-book-print-sheet">
          <RecipePrintCard recipe={viewRecipe} />
        </div>

        {sharedModals}
        <BetaFeedbackButton />
      </ResidenceLayout>
    );
  }

  /* ── Catalogue — a cookbook index, not an app grid ── */
  const focal = activeCategory
    ? `${activeCategory.label} — ${catalogue.length} ${catalogue.length === 1 ? "recipe" : "recipes"}.`
    : "The meals worth keeping.";

  return (
    <ResidenceLayout rail={<ResidenceRail currentKey="recipes" />} phase={phase} testid="module-page-recipe-book">
      <PageHeading
        testid="recipe-book-hero"
        module="meals"
        art="meals"
        room="Recipe Book"
        title="The Recipe Book"
        focal={focal}
        actions={
          <>
            <PillButton variant="solid" onClick={openAdd} testid="recipe-book-add-recipe-btn">
              Add recipe
            </PillButton>
            <TextLink onClick={() => setPhotoImportOpen(true)} testid="recipe-book-import-btn">
              Import from photo
            </TextLink>
          </>
        }
      />

      {all.length > 0 && (
        <nav
          className="flex flex-wrap items-center gap-x-8 gap-y-2 py-4 mb-10 border-y"
          style={{ borderColor: "var(--ara-line-soft)" }}
          aria-label="Recipe Book categories"
          data-testid="recipe-book-filter-nav"
        >
          <FilterButton active={!activeCategory} onClick={() => setCategoryId(null)} testid="recipe-book-filter-all">
            All recipes
          </FilterButton>
          {populatedCategories.map(cat => (
            <FilterButton
              key={cat.id}
              active={categoryId === cat.id}
              onClick={() => setCategoryId(cat.id)}
              testid={`recipe-book-filter-${cat.id}`}
            >
              {cat.label}
            </FilterButton>
          ))}
        </nav>
      )}

      <EditorialSection
        testid="recipe-book-catalogue"
        moduleMark={{ module: "meals", label: activeCategory ? activeCategory.label : "All recipes" }}
        title={activeCategory ? activeCategory.label : "Every recipe"}
        aside={activeCategory ? (
          <TextLink onClick={() => setCategoryId(null)} testid="recipe-book-clear-filter">
            ← All recipes
          </TextLink>
        ) : null}
      >
        {all.length === 0 ? (
          <div data-testid="recipe-book-empty">
            <p className="italic text-[19px] sm:text-[21px] leading-[1.4]" style={{ fontFamily: SERIF, color: "var(--ara-ink)", fontWeight: 400, maxWidth: "32ch" }}>
              Every family has recipes worth keeping. Add the first one.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3">
              <PillButton variant="solid" onClick={openAdd} testid="recipe-book-empty-add-btn">
                Add the first recipe
              </PillButton>
              <TextLink onClick={() => setPhotoImportOpen(true)} testid="recipe-book-empty-import-btn">
                Import from photo
              </TextLink>
            </div>
          </div>
        ) : catalogue.length === 0 ? (
          <div data-testid="recipe-book-category-empty">
            <p className="italic text-[19px] sm:text-[21px] leading-[1.4]" style={{ fontFamily: SERIF, color: "var(--ara-ink)", fontWeight: 400, maxWidth: "32ch" }}>
              No {activeCategory ? activeCategory.label.toLowerCase() : ""} recipes yet.
            </p>
            <div className="mt-6">
              <PillButton variant="solid" onClick={openAdd} testid="recipe-book-category-empty-add">
                Add the first
              </PillButton>
            </div>
          </div>
        ) : (
          <ul data-testid="recipe-book-list">
            {catalogue.map((r, i, arr) => {
              const total = (r.prepTime || 0) + (r.cookTime || 0);
              const meta = [
                total ? `${total} min` : null,
                r.serves ? `Serves ${r.serves}` : null,
                r.difficulty || null,
              ].filter(Boolean).join(" · ");
              return (
                <li
                  key={r.id}
                  className={`${i === 0 ? "border-t" : ""} ${i === arr.length - 1 ? "" : "border-b"}`}
                  style={{ borderColor: "var(--ara-line-soft)" }}
                >
                  <button
                    type="button"
                    onClick={() => setViewId(r.id)}
                    className="w-full grid grid-cols-[auto_1fr_auto] items-center gap-6 py-5 text-left transition-ara hover:opacity-70"
                    data-testid={`recipe-book-row-${r.id}`}
                  >
                    <RecipeImage recipe={r} size="thumb" />
                    <span className="min-w-0">
                      <span
                        className="block text-[19px] sm:text-[21px] leading-snug truncate"
                        style={{ fontFamily: SERIF, color: "var(--ara-ink)", fontWeight: 500 }}
                        data-testid={`recipe-book-row-title-${r.id}`}
                      >
                        {r.title}
                        {r.favourite && <span className="ml-2 text-[13px] align-middle" style={{ color: "var(--ara-mod-goals)" }}>♥</span>}
                      </span>
                      <span className="block mt-1.5 text-[12.5px] tracking-wide truncate" style={{ fontFamily: SANS, color: "var(--ara-ink-muted)", fontWeight: 400 }}>
                        {[r.category, meta].filter(Boolean).join("  ·  ") || r.subtitle || "—"}
                      </span>
                    </span>
                    <span className="text-[13px]" style={{ fontFamily: SANS, color: "var(--ara-ink-soft)", fontWeight: 400 }} aria-hidden>
                      →
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </EditorialSection>

      {all.length > 0 && (
        <div className="mt-2" data-testid="recipe-book-count-note">
          <Caption>{all.length} {all.length === 1 ? "recipe" : "recipes"} in the book</Caption>
        </div>
      )}

      <Colophon>ARA · Recipe Book</Colophon>

      <RecipePhotoImportModal
        open={photoImportOpen}
        onClose={() => setPhotoImportOpen(false)}
        onApply={(draft) => {
          setPhotoImportOpen(false);
          setEditId(null);
          setEditDraft(draft);
          setEditOpen(true);
        }}
      />
      {sharedModals}
      <BetaFeedbackButton />
    </ResidenceLayout>
  );
}

function FilterButton({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-[13px] transition-ara"
      style={{
        fontFamily: SANS,
        color: active ? "var(--ara-ink)" : "var(--ara-ink-soft)",
        fontWeight: active ? 500 : 400,
        borderBottom: active ? "1px solid var(--ara-ink)" : "1px solid transparent",
        paddingBottom: "2px",
      }}
      aria-current={active ? "true" : undefined}
      data-testid={testid}
    >
      {children}
    </button>
  );
}
